import {
    DataTestIdAfterPage,
    DataTestIdAfterGroup,
    DataTestIdAfterScopes,
    DataTestIdAfterElement,
    DataTestIdAfterIdentifier,
} from "./data-test-id.type";

/**
 * Service to generate data-testid attributes which are going to be used by the automation tests.
 * ! IMPORTANT: These data-testid attributes should be unique per page.
 * ! IMPORTANT: The values passed to the generator should be static to avoid breaking the automated tests.
 * So, to respect this rule, the generator is using multiple items to compose a unique data-testid.
 *
 * The generated data-testid follows the pattern:
 * `[page:<page_name>]--[group:<group_name>]--[scopes:<scope1_name|scope2_name>]--[element:<element_name>]--[id:<identifier>]`
 */
class DataTestId
    implements
        DataTestIdAfterPage,
        DataTestIdAfterGroup,
        DataTestIdAfterScopes,
        DataTestIdAfterElement,
        DataTestIdAfterIdentifier
{
    private _page: string = "";
    private _group: string = "";
    private _scopes: string[] = [];
    private _element: string = "";
    private _identifier: string = "";

    /**
     * Private constructor to prevent external instantiation.
     */
    constructor() {}

    /**
     * Set the page name.
     *
     * @param {string} page - The page name (typically the pathname).
     * @returns {DataTestIdAfterPage} - Returns interface for next available methods.
     */
    public page(page: string): DataTestIdAfterPage {
        this._page = page;

        return {
            group: (group: string) => this.group(group),
            scopes: (scopes: string[]) => this.scopes(scopes),
            element: (element: string) => this.element(element),
            identifier: (identifier: string) => this.identifier(identifier),
            build: () => this.build(),
        };
    }

    /**
     * Set the group name.
     *
     * @param {string} group - The group name.
     * @returns {DataTestIdAfterGroup} - Returns interface for next available methods.
     */
    public group(group: string): DataTestIdAfterGroup {
        this._group = group;

        return {
            scopes: (scopes: string[]) => this.scopes(scopes),
            element: (element: string) => this.element(element),
            identifier: (identifier: string) => this.identifier(identifier),
            build: () => this.build(),
        };
    }

    /**
     * Set the scopes.
     *
     * @param {string[]} scopes - The scopes to be used.
     * @returns {DataTestIdAfterScopes} - Returns interface for next available methods.
     */
    public scopes(scopes: string[]): DataTestIdAfterScopes {
        this._scopes = scopes;

        return {
            element: (element: string) => this.element(element),
            identifier: (identifier: string) => this.identifier(identifier),
            build: () => this.build(),
        };
    }

    /**
     * Set the element name.
     *
     * @param {string} element - The element name.
     * @returns {DataTestIdAfterElement} - Returns interface for next available methods.
     */
    public element(element: string): DataTestIdAfterElement {
        this._element = element;

        return {
            identifier: (identifier: string) => this.identifier(identifier),
            build: () => this.build(),
        };
    }

    /**
     * Set the identifier.
     *
     * @param {string} identifier - The identifier.
     * @returns {DataTestIdAfterIdentifier} - Returns interface for next available methods.
     */
    public identifier(identifier: string): DataTestIdAfterIdentifier {
        this._identifier = identifier;

        return {
            build: () => this.build(),
        };
    }

    /**
     * Build the data-testid string based on the configured values.
     *
     * @returns {string} - The generated data-testid string.
     *
     * @example
     * const dataTestId = new DataTestId()
     *     .page('/home')
     *     .group('form')
     *     .scopes(['input', 'text'])
     *     .element('username')
     *     .identifier('input1')
     *     .build();
     * // dataTestId will be:
     * // "[page:/home]--[group:form]--[scopes:input|text]--[element:username]--[id:input1]"
     *
     * @example
     * // Skip optional methods
     * const dataTestId = new DataTestId()
     *     .page('/home')
     *     .element('username')
     *     .build();
     * // dataTestId will be:
     * // "[page:/home]--[element:username]"
     */
    public build(): string {
        const parts: string[] = [];

        if (this._page) {
            parts.push(`[page:${this._page}]`);
        }

        if (this._group) {
            parts.push(`[group:${this._group}]`);
        }

        if (this._scopes.length && this._scopes.filter(Boolean).length) {
            parts.push(`[scopes:${this._scopes.filter(Boolean).join("|")}]`);
        }

        if (this._element) {
            parts.push(`[element:${this._element}]`);
        }

        if (this._identifier) {
            parts.push(`[id:${this._identifier}]`);
        }

        return parts.join("--");
    }
}

export default DataTestId;
